import {ChangeEvent, forwardRef, useCallback, useState} from "react";
import {noop} from "lodash";
import {X} from "lucide-react";

import {Input, InputProps} from "@/components/ui/input";
import {Tag} from "@/components/ui/Tag";
import {Button} from "@/components/ui/Button";

export interface MultiTagInputProps
  extends Omit<InputProps, "value" | "onChange"> {
  value?: string[];
  onChange?: (value: string[]) => void;
  maxItems?: number;
  label?: string;
}

const MultiTagInput = forwardRef<HTMLInputElement, MultiTagInputProps>(
  (props: MultiTagInputProps, ref) => {
    const {
      value = [],
      onChange = noop,
      placeholder = "Start typing to add",
      maxItems = 20,
      label,
      ...rest
    } = props;

    const [inputValue, setInputValue] = useState<string>("");

    const addTag = useCallback(() => {
      if (
        inputValue &&
        !value.includes(inputValue) &&
        value.length < maxItems
      ) {
        onChange([...value, inputValue]);
      }
      setInputValue("");
    }, [inputValue, onChange, value, maxItems]);

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      setInputValue(
        event.target.value.replace(
          /[^a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF\s]+/g,
          "",
        ),
      );
    }, []);

    const handleOnKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          addTag();
        }
      },
      [addTag],
    );

    const removeTag = useCallback(
      (tag: string) => {
        onChange(value.filter(t => t !== tag));
      },
      [onChange, value],
    );

    return (
      <section>
        {label && (
          <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
            {label}
          </label>
        )}
        <div className="relative border border-input rounded-md min-h-[40px] flex flex-wrap items-center gap-1 p-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          {value.map(tag => (
            <Tag
              className="cursor-pointer hover:border-slate-500/20 flex-shrink-0 text-sm"
              key={tag}
              variant="tertiary"
              onClick={() => removeTag(tag)}
            >
              {tag}
              <X className="ml-2 font-normal" width={14} />
            </Tag>
          ))}

          <input
            ref={ref}
            {...rest}
            onBlur={addTag}
            value={inputValue}
            onChange={event => handleChange(event)}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none placeholder:text-secondary text-sm"
            onKeyDown={handleOnKeyDown}
            placeholder={value.length === 0 ? placeholder : ""}
          />
        </div>
      </section>
    );
  },
);

MultiTagInput.displayName = "MultiTagInput";

export {MultiTagInput};
